<template>
  <div class="relative" ref="dataContainer"
  :class="{'cursor-pointer': hoveredDataPoint}"
  :style="{
    right: `${CANVAS_PADDING}px`,
    bottom: `${CANVAS_PADDING}px`,
    width: `calc(100% + ${CANVAS_PADDING * 2}px)`,
    height: `calc(100% + ${CANVAS_PADDING * 2}px)`
  }"
  @mousemove="handleMouseMove" @mouseleave="hoveredDataPoint = null, previewData = null"
  @click="handlePreviewClick">
    <canvas ref="dataCanvas" />
    <!-- Data Point Asset Hover Preview -->
    <transition name="fade">
      <div v-if="previewData" class="asset-preview-container anim-transform" :style="{ transform: tooltipTransform }">
        <BasePopupVideoPreview 
          v-if="previewData.type === 'video'"
          ref="previewVideoPlayer"
          class="relative"
          :title="previewData?.adName"
          :video-src="previewData.src"
          :attempt-manual-play="attemptManualPreviewPlayCounter"
          :width="previewWidth"
        >
          <template #footer>
            <slot name="preview-footer" :point="previewData" type="video" />
          </template>
        </BasePopupVideoPreview>
        <BasePopupThumbnailPreview
          v-else
          class="relative"
          :title="previewData?.adName"
          :thumbnail-src="previewData.src"
          :width="previewWidth"
        >
          <template #footer>
            <slot name="preview-footer" :point="previewData" type="image" />
          </template>
        </BasePopupThumbnailPreview>
      </div>
    </transition>
    <!-- Loading Indicator (if enabled) -->
    <PointLoadingIndicator 
      v-if="loadingIndicatorData"
      class="point-loading-indicator"
      :color="loadingIndicatorData.color"
      :style="{ transform: pointLoadingIndicatorTransform }"
    />
  </div>
</template>

<script>
import { quadtree } from 'd3-quadtree'
import PointLoadingIndicator from './PointLoadingIndicator.vue'

const DATA_POINT_RADIUS = 3 // px
const CANVAS_PADDING = DATA_POINT_RADIUS + 10 // px
const DATA_POINT_HOVER_THRESHOLD = 10 // px
const TOOLTIP_VIEWPORT_BUFFER = 32 // px

const MAX_COMPUTE_RETRIES = 3

export default {
  name: 'DataRenderer',
  components: {
    PointLoadingIndicator
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    // 2-element array of D3 linear scales for the x and y axes
    axisScales: {
      type: Array,
      default: () => []
    },
    uidKey: {
      type: String,
      required: true
    },
    xAxisValueKey: {
      type: String,
      required: true
    },
    yAxisValueKey: {
      type: String,
      default: 'spend'
    },
    // Object with width and height properties for the graph render area
    dataAreaDimensions: {
      type: Object,
      default: () => {}
    },
    isActiveTab: {
      type: Boolean,
      default: true
    },
    enableDetailsLoadHandler: {
      type: Boolean,
      default: false
    },
    loadingPointDetailsId: {
      type: String,
      default: null
    },
    previewWidth: {
      type: Number,
      default: 120
    }
  },
  data () {
    return {
      dataPoints: [],
      hoveredDataPoint: null,
      previewData: null,
      pointQuadTree: null,
      loadingIndicatorData: null,
      computeRetries: 0,
      attemptManualPreviewPlayCounter: 0,
      browserDPR: 1,
      mainLayoutHasScrollbar: false,
      mainLayoutObserver: null,
      CANVAS_PADDING,
      DATA_POINT_RADIUS
    }
  },
  mounted () {
    this.$nextTick(() => { this.computeDataPoints() })
    
    // Define handlers for browser zoom (recompute if browser zoom changes)
    if (window.visualViewport) {
      this.browserDPR = window.devicePixelRatio
      window.visualViewport.addEventListener('resize', this.handleBrowserZoom)
    }

    // Define handler for main layout scrollbar appearance (recompute if scrollbar appears/disappears)
    const mainLayout = document.querySelector('.main-layout')
    this.handleMainLayoutScrollbarAppearance(mainLayout)
    if (mainLayout) {
      this.mainLayoutObserver = new ResizeObserver(() => {
        this.handleMainLayoutScrollbarAppearance(mainLayout)
      })
      this.mainLayoutObserver.observe(mainLayout)
    }
  },
  beforeDestroy () {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleBrowserZoom)
    }
    if (this.mainLayoutObserver) {
      this.mainLayoutObserver.disconnect()
      this.mainLayoutObserver = null
    }
  },
  watch: {
    data () {
      this.$nextTick(() => { this.computeDataPoints() })
    },
    hoveredDataPoint () {
      this.drawCanvas()
    },
    loadingPointDetailsId (newValue) {
      if (!newValue) {
        this.loadingIndicatorData = null
        this.hoveredDataPoint = null
        this.previewData = null
      }
    }
  },
  computed: {
    tooltipTransform () {
      if (!this.previewData) return 'translate(0, 0)'
      const { xTransform, yTransform } = this.previewData
      const xStr = `calc(-50% + ${xTransform}px)`
      const yStr = `calc(100% - ${yTransform}px)`
      return `translate(${xStr}, ${yStr})`
    },
    pointLoadingIndicatorTransform () {
      if (!this.loadingIndicatorData) return 'translate(0, 0)'
      const { xPos, yPos } = this.loadingIndicatorData
      const xStr = `calc(${xPos}px - 50%)`
      const yStr = `calc(${-yPos}px + 50%)`
      return `translate(${xStr}, ${yStr})`
    }
  },
  methods: {
    computeDataPoints () {
      if (!this.isActiveTab) return
      // Nothing to plot - wipe any points left over from the previous dataset
      if (!this.data?.length) {
        this.clearDataPoints()
        return
      }
      if (!this.axisScales?.length || !this.dataAreaDimensions?.width) {
        console.log(`Missing necessary data for chart rendering, retrying in 50ms (${this.computeRetries}/${MAX_COMPUTE_RETRIES})`)
        this.clearDataPoints()
        if (this.computeRetries < MAX_COMPUTE_RETRIES) {
          this.computeRetries++
          setTimeout(() => { this.computeDataPoints() }, 50)
        }
        return
      }
      this.computeRetries = 0
      const [ xScale, yScale ] = this.axisScales
      // We have to expand the bounds of the render area to account for points rendered on the edges
      const availableWidth = this.dataAreaDimensions.width + CANVAS_PADDING * 2
      const availableHeight = this.dataAreaDimensions.height + CANVAS_PADDING * 2
      // Adjust the scales' output ranges to account for this canvas 'padding'
      xScale.range([CANVAS_PADDING, availableWidth - CANVAS_PADDING])
      yScale.range([CANVAS_PADDING, availableHeight - CANVAS_PADDING])

      const dataPointsArr = this.data.map(member => {
        const xValue = member[this.xAxisValueKey]
        const yValue = member[this.yAxisValueKey]
        const xPos = Math.round(xScale(xValue))
        const yPos = Math.round(yScale(yValue))
        return {
          data: member,
          uid: member[this.uidKey],
          xPos,
          yPos,
          pointColor: member.pointColor ?? '#40C4AA',
          renderOnTop: member.renderOnTop ?? false,
        }
      })
      this.dataPoints = dataPointsArr

      // Populate the quadtree for mouseover hit detection
      this.pointQuadTree = quadtree()
        .x(d => d.xPos)
        .y(d => d.yPos)
        .addAll(dataPointsArr)

      // --- RENDER THE DATA POINTS IN THE CANVAS ---
      this.drawCanvas()
    },
    // Drops every rendered point along with anything derived from them (hit detection, hover preview)
    clearDataPoints () {
      this.dataPoints = []
      this.pointQuadTree = null
      this.hoveredDataPoint = null
      this.previewData = null
      this.clearCanvas()
    },
    clearCanvas () {
      const canvas = this.$refs.dataCanvas
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) return
      // Clear against the canvas' raw pixel bounds, ignoring whatever transform the last draw left behind
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    },
    drawCanvas () {
      // We have to expand the bounds of the render area to account for points rendered on the edges
      const availableWidth = this.dataAreaDimensions.width + CANVAS_PADDING * 2
      const availableHeight = this.dataAreaDimensions.height + CANVAS_PADDING * 2

      const canvas = this.$refs.dataCanvas
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) {
        console.error("Couldn't access canvas ref/context for data rendering")
        return
      }

      // Scale the canvas for high-DPI displays
      const devicePixelRatio = window.devicePixelRatio || 1
      canvas.style.width = `${availableWidth}px`
      canvas.style.height = `${availableHeight}px`
      canvas.width = availableWidth * devicePixelRatio
      canvas.height = availableHeight * devicePixelRatio
      ctx.setTransform(1, 0, 0, 1, 0, 0) // Reset the transform matrix
      ctx.scale(devicePixelRatio, devicePixelRatio) // Apply the dpi scale
      // Change coordinate origin to bottom-left
      ctx.translate(0, availableHeight)
      ctx.scale(1, -1)

      // Clear the canvas
      ctx.clearRect(0, 0, availableWidth, availableHeight)

      const drawPoint = (xPos, yPos, color) => {
        ctx.beginPath()
        ctx.lineWidth = 1
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()
      }
      
      // Render the data points
      const activePoints = []
      for (let i = 0; i < this.dataPoints.length; i++) {
        const point = this.dataPoints[i]
        if (this.hoveredDataPoint && this.hoveredDataPoint?.uid === point.uid) continue
        if (point.renderOnTop) {
          activePoints.push(point)
          continue
        }
        drawPoint(point.xPos, point.yPos, point.pointColor)
      }
      // Render the active points (do them last so they're on top)
      for (let i = 0; i < activePoints.length; i++) {
        const { xPos, yPos, pointColor } = activePoints[i]
        drawPoint(xPos, yPos, pointColor)
      }
      // Render the hovered point (if it exists)
      if (this.hoveredDataPoint) {
        const { xPos, yPos, pointColor } = this.hoveredDataPoint
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS + 6, 0, Math.PI * 2)
        ctx.fillStyle = '#FFFFFF'
        ctx.fill()
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = pointColor
        ctx.fill()
        ctx.beginPath()
        ctx.arc(xPos, yPos, DATA_POINT_RADIUS + 6, 0, Math.PI * 2)
        ctx.strokeStyle = pointColor
        ctx.lineWidth = 2
        ctx.stroke()
      }
    },
    handleMouseMove (event) {
      if (!this.$refs.dataContainer || !this.pointQuadTree) {
        if (this.hoveredDataPoint) this.hoveredDataPoint = null
        return
      }
      const { clientX, clientY } = event
      const renderArea = this.$refs.dataContainer
      const renderAreaRect = renderArea.getBoundingClientRect()
      const mouseX = clientX - renderAreaRect.left
      const mouseY = renderArea.clientHeight - (clientY - renderAreaRect.top) // convert to canvas coordinates

      // Find the closest point to the mouse position (within a threshold)
      const nearestPoint = this.pointQuadTree.find(mouseX, mouseY, DATA_POINT_HOVER_THRESHOLD)
      this.hoveredDataPoint = nearestPoint ?? null
      this.updatePreviewData(nearestPoint)
    },
    updatePreviewData: _.debounce(function (hoveredDataPoint) {
      if (!hoveredDataPoint) {
        this.previewData = null
        return
      }
      const { xPos, yPos, data } = hoveredDataPoint

      // Extract the asset src and determine its type
      let type; let src; let adName = null;
      if (data.asset) {
        // Prefer the asset type given by the API, fall back to sniffing the asset extension
        const extension = data.asset.split('.').pop()
        type = data.type ?? (extension === 'mp4' ? 'video' : 'image')
        src = data.asset
      } else {
        type = data?.video_url_source?.length ? 'video' : 'image'
        src = type === 'video' ? data?.video_url_source : data?.ad_image_url
        adName = data?.ad_name
      }

      // Compute the transform offset
      const xTransform = this.getClampedTooltipX(xPos)
      const yTransform = yPos - 14 // 14px y-gap

      this.previewData = { src, type, adName, xTransform, yTransform, data }
    }, 20),
    getClampedTooltipX (xPos) {
      // Shift the tooltip horizontally so it stays fully on screen when its point is near a viewport edge
      const containerRect = this.$refs.dataContainer?.getBoundingClientRect()
      if (!containerRect) return xPos
      const halfWidth = this.previewWidth / 2
      const minCenter = TOOLTIP_VIEWPORT_BUFFER + halfWidth
      const maxCenter = document.documentElement.clientWidth - TOOLTIP_VIEWPORT_BUFFER - halfWidth
      const centerX = Math.max(minCenter, Math.min(maxCenter, containerRect.left + xPos))
      return centerX - containerRect.left
    },
    handlePreviewClick () {
      const videoPlayer = this.$refs.previewVideoPlayer
      if (videoPlayer && videoPlayer.getIsPlaybackBlocked()) {
        this.attemptManualPreviewPlayCounter++
        return
      }
      if (this.hoveredDataPoint) {
        const { xPos, yPos, data } = this.hoveredDataPoint
        this.$emit('point-click', data)
        if (this.enableDetailsLoadHandler && !this.loadingPointDetailsId && data.id !== this.loadingPointDetailsId) {
          this.loadingIndicatorData = { xPos, yPos, color: data.pointColor }
          this.$emit('update:loadingPointDetailsId', data.id)
        } else {
          this.hoveredDataPoint = null
          this.previewData = null
        }
      }
    },
    handleBrowserZoom () {
      if (this.browserDPR !== window.devicePixelRatio) {
        this.browserDPR = window.devicePixelRatio
        setTimeout(() => { this.computeDataPoints() }, 50)
      }
    },
    handleMainLayoutScrollbarAppearance (domRef) {
      const doesOverflow = domRef.scrollHeight > domRef.clientHeight
      if (doesOverflow !== this.mainLayoutHasScrollbar) {
        this.mainLayoutHasScrollbar = doesOverflow
        setTimeout(() => { this.computeDataPoints() }, 50)
      }
    },
    resetComputeRetries () {
      this.computeRetries = 0
    }
  }
}
</script>

<style scoped>
.asset-preview-container {
  position: absolute;
  bottom: 0;
  left: 0;
  transform-origin: top center;
  z-index: 60000;
}
.asset-preview-container.anim-transform {
  transition: transform 75ms ease-in-out;
}
.point-loading-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  transform-origin: center;
  z-index: 100;
}

.fade-enter-active {
  animation: fade-in 75ms ease-in-out forwards;
}
.fade-leave-active {
  animation: fade-out 75ms ease-in-out forwards;
}
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fade-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
</style>